import { parseVisitsMarkdown } from './markdownParser.js';
import type { PubDatabase } from './types.js';

/**
 * Loads and parses the visits.md file
 * @returns Promise resolving to array of pubs
 * @throws Error if file cannot be loaded or parsed
 */
export async function loadPubData(): Promise<PubDatabase> {
  try {
    console.log('Loading pub data from /data/visits.md...');

    // Fetch the markdown file from the public directory
    const response = await fetch('/data/visits.md');

    if (!response.ok) {
      throw new Error(`Failed to load visits.md: ${response.status} ${response.statusText}`);
    }

    const markdownText = await response.text();

    console.log(`Markdown loaded (${markdownText.length} chars)`);

    // Parse markdown into Pub objects
    const pubs = parseVisitsMarkdown(markdownText);

    console.log(`Parser returned ${pubs.length} pubs`);

    if (pubs.length === 0) {
      throw new Error('No pubs found in visits.md format: expected array of pubs');
    }

    console.log(`Successfully loaded ${pubs.length} pubs from markdown file`);

    return pubs;
  } catch (error) {
    console.error('Error loading pub data:', error);
    throw error;
  }
}

/**
 * Gets statistics about the pub database
 * @param pubs - Array of pubs
 * @returns Statistics object
 */
export function getPubStats(pubs: PubDatabase) {
  const total = pubs.length;
  const visited = pubs.filter((p) => p.visited).length;
  const unvisited = total - visited;
  const closed = pubs.filter((p) => p.closed).length;

  return {
    total,
    visited,
    unvisited,
    closed,
    visitedPercentage: total > 0 ? Math.round((visited / total) * 100) : 0,
  };
}
